import type { ChatMessage, ChatbotOptions, JsonValue } from "./types";
import { AiChatWebClient } from "./client";
import { Header } from "./ui/header";
import { MessageList } from "./ui/list";
import { InputArea } from "./ui/input";
import { createThemeStyles } from "./ui/theme";
import { Launcher } from "./ui/launcher";

type ChatbotEvent = "message" | "error" | "ready" | "toggle";
type Listener = (payload: unknown) => void;

export class AiChatChatbot {
  private readonly client: AiChatWebClient;
  private readonly agentId: string;
  private readonly mount: HTMLElement;
  private readonly title: string;
  private readonly subtitle: string;
  private readonly placeholder: string;
  private readonly welcomeMessage?: string;
  private readonly persistConversation: boolean;
  private readonly storageKey: string;
  private readonly position: "inline" | "bottom-right" | "bottom-left";
  private readonly showTimestamps: boolean;
  private readonly maxHeight: string;
  private readonly width: string;
  private readonly onApprove?: ChatbotOptions["onApprove"];
  private readonly onReject?: ChatbotOptions["onReject"];

  private conversationId: string | null = null;
  private readonly messages: ChatMessage[] = [];
  private readonly listeners: Map<ChatbotEvent, Set<Listener>> = new Map();
  private unreadCount = 0;

  private root?: ShadowRoot;
  private container?: HTMLElement;
  private header?: Header;
  private list?: MessageList;
  private input?: InputArea;
  private launcher?: Launcher;
  private isOpen = false;

  constructor(options: ChatbotOptions) {
    this.client = options.client;
    this.agentId = options.agentId;
    this.mount =
      typeof options.mount === "string"
        ? this.resolveMount(options.mount)
        : options.mount;
    this.title = options.title ?? "Assistant";
    this.subtitle = options.subtitle ?? "Online";
    this.placeholder = options.placeholder ?? "Ask me anything…";
    this.welcomeMessage = options.welcomeMessage;
    this.persistConversation = options.persistConversation ?? false;
    this.storageKey = options.storageKey ?? "aichat_conversation_id";
    this.position = options.position ?? "inline";
    this.showTimestamps = options.showTimestamps ?? false;
    this.maxHeight = options.maxHeight ?? "560px";
    this.width = options.width ?? "400px";
    this.onApprove = options.onApprove;
    this.onReject = options.onReject;

    if (this.persistConversation) {
      this.conversationId = localStorage.getItem(this.storageKey);
    }

    this.render(options.theme);

    if (this.welcomeMessage) {
      this.addMessage({ role: "assistant", content: this.welcomeMessage, timestamp: Date.now() });
    }

    this.emit("ready", null);
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  on(event: ChatbotEvent, listener: Listener): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)?.add(listener);
  }

  off(event: ChatbotEvent, listener: Listener): void {
    this.listeners.get(event)?.delete(listener);
  }

  toggle(force?: boolean): void {
    this.isOpen = force ?? !this.isOpen;
    this.container?.classList.toggle("ac-open", this.isOpen);
    this.launcher?.setActive(this.isOpen);
    if (this.isOpen) {
      this.unreadCount = 0;
      this.input?.focus();
    }
    this.emit("toggle", { isOpen: this.isOpen });
  }

  destroy(): void {
    this.listeners.clear();
    if (this.root && this.container) this.root.removeChild(this.container);
    if (this.launcher) this.root?.removeChild(this.launcher.getElement());
  }

  // ─── Private ────────────────────────────────────────────────────────────

  private emit(event: ChatbotEvent, payload: unknown): void {
    this.listeners.get(event)?.forEach((l) => l(payload));
  }

  private resolveMount(selector: string): HTMLElement {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) throw new Error(`Mount element not found: ${selector}`);
    return el;
  }

  private render(themeOptions?: ChatbotOptions["theme"]): void {
    this.root = this.mount.attachShadow({ mode: "open" });

    const themeStyle = document.createElement("style");
    themeStyle.textContent = createThemeStyles(themeOptions);

    const baseStyle = document.createElement("style");
    baseStyle.textContent = this.getStyles();

    this.container = document.createElement("div");
    this.container.className = `ac-widget ac-pos-${this.position}`;
    this.container.style.setProperty("--ac-max-height", this.maxHeight);
    this.container.style.setProperty("--ac-width", this.width);

    if (this.position === "inline") {
      this.isOpen = true;
      this.container.classList.add("ac-open");
    }

    this.header = new Header(
      this.title,
      this.subtitle,
      this.position !== "inline" ? () => this.toggle(false) : undefined,
    );

    this.list = new MessageList(
      this.showTimestamps,
      (toolId, toolName, args) => this.onApprove?.(toolId, toolName, args),
      (toolId, toolName) => this.onReject?.(toolId, toolName),
    );

    this.input = new InputArea(this.placeholder, (val) => this.handleSend(val));

    this.container.appendChild(this.header.getElement());
    this.container.appendChild(this.list.getElement());
    this.container.appendChild(this.input.getElement());

    this.root.appendChild(themeStyle);
    this.root.appendChild(baseStyle);
    this.root.appendChild(this.container);

    if (this.position !== "inline") {
      this.launcher = new Launcher(() => this.toggle());
      this.root.appendChild(this.launcher.getElement());
    }
  }

  private async handleSend(content: string): Promise<void> {
    const userMessage: ChatMessage = { role: "user", content, timestamp: Date.now() };
    this.addMessage(userMessage);

    this.input?.setLoading(true);
    this.header?.setStatus("thinking");
    const typingId = this.addTyping();

    try {
      const response = await this.client.sendMessage({
        agentId: this.agentId,
        message: content,
        conversationId: this.conversationId ?? undefined,
      });

      if (!response.success) throw new Error(response.error ?? "Unknown error");

      this.conversationId = response.conversationId ?? this.conversationId;
      if (this.persistConversation && this.conversationId) {
        localStorage.setItem(this.storageKey, this.conversationId);
      }

      this.removeTyping(typingId);

      // Handle HITL approval flow
      const meta = response.metadata;
      if (meta?.["approval_required"] === true) {
        const approvalMsg: ChatMessage = {
          role: "assistant",
          content: response.message,
          timestamp: Date.now(),
          approvalRequired: true,
          approvalMeta: {
            toolName: String(meta["tool_name"] ?? ""),
            toolId: String(meta["tool_id"] ?? ""),
            arguments: (meta["arguments"] as Record<string, JsonValue>) ?? {},
          },
        };
        this.addMessage(approvalMsg);
      } else {
        this.addMessage({ role: "assistant", content: response.message, timestamp: Date.now() });
      }

      if (!this.isOpen) {
        this.unreadCount++;
        this.launcher?.showBadge(this.unreadCount);
      }

      this.emit("message", response);
    } catch (err) {
      this.removeTyping(typingId);
      this.addMessage({
        role: "assistant",
        content: "Sorry, something went wrong. Please try again.",
        timestamp: Date.now(),
      });
      this.emit("error", err);
    } finally {
      this.input?.setLoading(false);
      this.header?.setStatus("online");
    }
  }

  private addMessage(message: ChatMessage): void {
    this.messages.push(message);
    this.list?.addMessage(message);
  }

  private addTyping(): string {
    const id = "typing-" + Math.random().toString(36).slice(2);
    this.list?.addTyping(id);
    return id;
  }

  private removeTyping(id: string): void {
    this.list?.removeTyping(id);
  }

  // ─── Styles ─────────────────────────────────────────────────────────────

  private getStyles(): string {
    return `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .ac-widget {
        --ac-max-height: 560px;
        --ac-width: 400px;
        width: var(--ac-width);
        max-width: 100%;
        height: var(--ac-max-height);
        background: var(--ac-widget-bg);
        border: 1px solid var(--ac-widget-border);
        border-radius: var(--ac-radius);
        box-shadow: var(--ac-shadow);
        display: grid;
        grid-template-rows: auto 1fr auto;
        font-family: var(--ac-font);
        font-size: var(--ac-font-size);
        overflow: hidden;
        opacity: 0;
        visibility: hidden;
        transform: translateY(12px) scale(0.98);
        transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.34,1.56,0.64,1), visibility 0.25s;
      }
      .ac-widget.ac-open {
        opacity: 1;
        visibility: visible;
        transform: translateY(0) scale(1);
      }
      .ac-pos-inline { position: relative; }
      .ac-pos-bottom-right { position: fixed; bottom: 84px; right: 20px; z-index: 9999; }
      .ac-pos-bottom-left  { position: fixed; bottom: 84px; left: 20px;  z-index: 9999; }

      /* Header */
      .ac-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        background: var(--ac-header-bg);
        color: var(--ac-header-text);
        border-bottom: 1px solid rgba(255,255,255,0.07);
        gap: 12px;
        flex-shrink: 0;
      }
      .ac-header-info {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
        flex-wrap: wrap;
      }
      .ac-header-title {
        font-family: var(--ac-display-font);
        font-size: 15px;
        font-weight: 400;
        letter-spacing: 0.01em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .ac-header-subtitle {
        display: block;
        font-size: 10px;
        opacity: 0.5;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        white-space: nowrap;
        width: 100%;
        padding-left: 18px;
      }
      .ac-status-dot {
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        transition: background 0.3s;
      }
      .ac-status-online  { background: #4ade80; box-shadow: 0 0 0 2px rgba(74,222,128,0.25); }
      .ac-status-thinking {
        background: var(--ac-accent);
        box-shadow: 0 0 0 2px rgba(201,168,76,0.3);
        animation: ac-pulse 0.9s infinite alternate ease-in-out;
      }
      .ac-status-offline { background: #6b7280; }
      @keyframes ac-pulse {
        from { transform: scale(0.8); opacity: 0.6; }
        to   { transform: scale(1.2); opacity: 1; }
      }
      .ac-header-close {
        flex-shrink: 0;
        background: none;
        border: none;
        color: var(--ac-header-text);
        opacity: 0.45;
        cursor: pointer;
        padding: 5px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.15s, background 0.15s;
      }
      .ac-header-close:hover { opacity: 1; background: rgba(255,255,255,0.1); }

      /* Messages */
      .ac-messages {
        padding: 16px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 10px;
        scroll-behavior: smooth;
      }
      .ac-messages::-webkit-scrollbar { width: 4px; }
      .ac-messages::-webkit-scrollbar-track { background: transparent; }
      .ac-messages::-webkit-scrollbar-thumb { background: var(--ac-widget-border); border-radius: 2px; }

      .ac-msg-wrap {
        display: flex;
        flex-direction: column;
        gap: 3px;
        animation: ac-fadein 0.2s ease both;
      }
      @keyframes ac-fadein {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .ac-msg-user      { align-items: flex-end; }
      .ac-msg-assistant { align-items: flex-start; }

      /* Bubbles */
      .ac-bubble {
        max-width: 82%;
        padding: 10px 14px;
        border-radius: var(--ac-radius);
        line-height: 1.6;
        word-break: break-word;
      }
      .ac-bubble-user {
        background: var(--ac-user-bg);
        color: var(--ac-user-text);
        border-bottom-right-radius: var(--ac-radius-xs);
      }
      .ac-bubble-assistant {
        background: var(--ac-bot-bg);
        color: var(--ac-bot-text);
        border-bottom-left-radius: var(--ac-radius-xs);
      }

      /* Markdown inside bubbles */
      .ac-bubble .ac-p { display: block; }
      .ac-bubble .ac-p + .ac-p { margin-top: 0.4em; }
      .ac-bubble .ac-spacer { height: 0.3em; }
      .ac-bubble strong { font-weight: 600; }
      .ac-bubble em { font-style: italic; }
      .ac-bubble a { color: inherit; text-decoration: underline; font-weight: 500; }
      .ac-bubble .ac-h1 { font-family: var(--ac-display-font); font-size: 1.2em; font-weight: 400; margin: 0.6em 0 0.25em; }
      .ac-bubble .ac-h2 { font-size: 1.05em; font-weight: 600; margin: 0.5em 0 0.2em; }
      .ac-bubble .ac-h3 { font-size: 0.95em; font-weight: 600; margin: 0.5em 0 0.15em; opacity: 0.75; }
      .ac-bubble .ac-list { margin: 0.4em 0 0.4em 1.1em; display: flex; flex-direction: column; gap: 2px; }
      .ac-bubble .ac-list-ol { list-style-type: decimal; }
      .ac-bubble .ac-list:not(.ac-list-ol) { list-style-type: disc; }
      .ac-bubble .ac-code-inline {
        font-family: 'DM Mono', 'Fira Code', ui-monospace, monospace;
        font-size: 0.84em;
        padding: 1px 5px;
        border-radius: 4px;
        background: rgba(0,0,0,0.08);
      }
      .ac-bubble-user .ac-code-inline { background: rgba(255,255,255,0.15); }
      .ac-bubble .ac-code-block {
        display: block;
        margin: 0.5em 0;
        padding: 10px 12px;
        border-radius: var(--ac-radius-xs);
        background: rgba(0,0,0,0.1);
        font-family: 'DM Mono', 'Fira Code', ui-monospace, monospace;
        font-size: 0.82em;
        overflow-x: auto;
        white-space: pre;
        line-height: 1.5;
      }
      .ac-bubble-user .ac-code-block { background: rgba(255,255,255,0.12); }
      .ac-bubble .ac-blockquote {
        border-left: 3px solid var(--ac-accent);
        padding-left: 10px;
        opacity: 0.75;
        font-style: italic;
        margin: 0.4em 0;
      }
      .ac-bubble .ac-hr { border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 0.5em 0; }

      /* Timestamp */
      .ac-timestamp {
        font-size: 10px;
        opacity: 0.35;
        letter-spacing: 0.03em;
        padding: 0 2px;
      }

      /* Typing indicator */
      .ac-typing-indicator {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 12px 14px;
        background: var(--ac-bot-bg);
        border-radius: var(--ac-radius);
        border-bottom-left-radius: var(--ac-radius-xs);
        width: fit-content;
      }
      .ac-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--ac-bot-text);
        opacity: 0.3;
        animation: ac-bounce 1.2s infinite ease-in-out;
      }
      .ac-dot:nth-child(1) { animation-delay: 0s; }
      .ac-dot:nth-child(2) { animation-delay: 0.18s; }
      .ac-dot:nth-child(3) { animation-delay: 0.36s; }
      @keyframes ac-bounce {
        0%, 80%, 100% { transform: translateY(0);    opacity: 0.3; }
        40%            { transform: translateY(-5px); opacity: 0.9; }
      }

      /* HITL Approval card */
      .ac-approval-card {
        max-width: 92%;
        border: 1px solid var(--ac-accent);
        border-radius: var(--ac-radius-sm);
        background: var(--ac-widget-bg);
        color: var(--ac-input-text);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .ac-approval-icon { padding: 12px 14px 0; color: var(--ac-accent); }
      .ac-approval-body { padding: 8px 14px 14px; display: flex; flex-direction: column; gap: 8px; }
      .ac-approval-title { font-size: 13px; font-weight: 600; }
      .ac-approval-desc { font-size: 12px; opacity: 0.6; line-height: 1.5; }
      .ac-approval-args {
        font-family: 'DM Mono', 'Fira Code', ui-monospace, monospace;
        font-size: 11px;
        background: rgba(0,0,0,0.04);
        border-radius: var(--ac-radius-xs);
        padding: 8px 10px;
        overflow-x: auto;
        white-space: pre;
        border: 1px solid var(--ac-widget-border);
        max-height: 100px;
      }
      .ac-approval-actions { display: flex; gap: 8px; }
      .ac-approval-btn {
        flex: 1;
        padding: 7px 12px;
        border-radius: var(--ac-radius-xs);
        border: none;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        font-family: var(--ac-font);
        transition: opacity 0.15s, transform 0.1s;
      }
      .ac-approval-btn:hover  { opacity: 0.85; transform: translateY(-1px); }
      .ac-approval-btn:active { transform: translateY(0); }
      .ac-approval-approve { background: var(--ac-primary); color: var(--ac-header-text); }
      .ac-approval-reject  { background: transparent; color: var(--ac-input-text); border: 1px solid var(--ac-widget-border); }
      .ac-approval-resolved { font-size: 12px; font-weight: 500; padding: 4px 0; }
      .ac-approval-approved { color: #4ade80; }
      .ac-approval-rejected { color: #f87171; }

      /* Input area */
      .ac-input-wrap {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 10px 12px;
        border-top: 1px solid var(--ac-widget-border);
        background: var(--ac-input-bg);
        flex-shrink: 0;
      }
      .ac-input-field {
        flex: 1;
        resize: none;
        border: 1px solid var(--ac-input-border);
        border-radius: var(--ac-radius-sm);
        padding: 9px 12px;
        font-size: var(--ac-font-size);
        font-family: var(--ac-font);
        color: var(--ac-input-text);
        background: var(--ac-input-bg);
        outline: none;
        min-height: 38px;
        max-height: 120px;
        line-height: 1.5;
        transition: border-color 0.15s;
        overflow-y: auto;
      }
      .ac-input-field:focus        { border-color: var(--ac-primary); }
      .ac-input-field::placeholder { opacity: 0.4; }
      .ac-input-field:disabled     { opacity: 0.5; cursor: not-allowed; }

      .ac-send-btn {
        flex-shrink: 0;
        width: 38px;
        height: 38px;
        border-radius: var(--ac-radius-sm);
        border: none;
        background: var(--ac-primary);
        color: var(--ac-header-text);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.15s, transform 0.1s;
      }
      .ac-send-btn:hover:not(:disabled)  { opacity: 0.85; transform: translateY(-1px); }
      .ac-send-btn:active:not(:disabled) { transform: translateY(0); }
      .ac-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .ac-send-btn .ac-spin { animation: ac-rotate 0.8s linear infinite; }
      @keyframes ac-rotate { to { transform: rotate(360deg); } }

      /* Launcher */
      .ac-launcher {
        position: fixed;
        bottom: 20px;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: var(--ac-primary);
        color: var(--ac-header-text);
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 20px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s;
        z-index: 10000;
      }
      .ac-launcher:hover { transform: scale(1.08); box-shadow: 0 6px 28px rgba(0,0,0,0.28); }
      .ac-pos-bottom-right ~ .ac-launcher { right: 20px; }
      .ac-pos-bottom-left  ~ .ac-launcher { left: 20px; }

      .ac-launcher-icon {
        position: absolute;
        transition: opacity 0.2s, transform 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .ac-launcher-close { opacity: 0; transform: rotate(-90deg) scale(0.7); }
      .ac-launcher-open  { opacity: 1; transform: rotate(0deg) scale(1); }
      .ac-launcher-active .ac-launcher-open  { opacity: 0; transform: rotate(90deg) scale(0.7); }
      .ac-launcher-active .ac-launcher-close { opacity: 1; transform: rotate(0deg) scale(1); }

      .ac-launcher-badge {
        position: absolute;
        top: 0;
        right: 0;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 9px;
        background: var(--ac-accent);
        color: #1a1a2e;
        font-size: 10px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid var(--ac-widget-bg);
        animation: ac-pop 0.3s cubic-bezier(0.34,1.56,0.64,1);
      }
      @keyframes ac-pop { from { transform: scale(0); } to { transform: scale(1); } }
    `;
  }
}
